import React, { useMemo } from 'react';
import { Badge, Callout, Flex, Text } from '@/components/ui';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { StudyForm } from '@/types/studyForms';
import type { CsvFile } from '@/types/csvFiles';
import { TerminalErrorDisplay, TerminalErrorGroup } from './TerminalErrorDisplay';
import { StudyFormHoverCard } from './StudyFormHoverCard';

export interface ImportValidationErrorsProps {
  errors:
    | {
        [studyFormId: string]: string | { forms?: Record<string, string>; files?: Record<string, string> } | Record<string, unknown>;
      }
    | {
        forms?: Record<string, string>;
        files?: Record<string, string>;
      }
    | Record<string, unknown>;
  studyForms?: Map<number, StudyForm>;
  csvFiles?: Map<number, CsvFile>;
}

/**
 * Normalizes error messages from different backend formats to a string
 * Handles:
 * - String: returns as-is
 * - Object with forms/files: extracts messages from forms
 * - Other objects: converts to JSON string or extracts values
 */
function normalizeErrorMessage(
  error: string | { forms?: Record<string, string>; files?: Record<string, string> } | Record<string, unknown>
): string {
  // If it's already a string, return it
  if (typeof error === 'string') {
    return error;
  }

  // If it's an object with forms property, extract messages from forms
  if (error && typeof error === 'object' && 'forms' in error && error.forms) {
    const forms = error.forms as Record<string, string>;
    const messages = Object.values(forms).filter(Boolean);
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  // If it's an object with files property (but no forms), extract messages from files
  if (error && typeof error === 'object' && 'files' in error && error.files) {
    const files = error.files as Record<string, string>;
    const messages = Object.values(files).filter(Boolean);
    if (messages.length > 0) {
      return messages.join('; ');
    }
  }

  // For other objects, try to extract string values or convert to JSON
  if (error && typeof error === 'object') {
    const values = Object.values(error).filter(v => typeof v === 'string' && v.length > 0);
    if (values.length > 0) {
      return values.join('; ');
    }
    // Last resort: convert to JSON string
    try {
      return JSON.stringify(error);
    } catch {
      return 'Validation error';
    }
  }

  // Fallback
  return 'Validation error';
}

export const ImportValidationErrors: React.FC<ImportValidationErrorsProps> = ({
  errors,
  studyForms,
  csvFiles,
}) => {
  // Parse errors into TerminalErrorDisplay format
  const errorGroups = useMemo((): TerminalErrorGroup[] => {
    // Structure to store errors separately for forms and files
    const formErrors: Record<string, string[]> = {};
    const fileErrors: Record<string, { fileId: number; message: string }> = {};
    
    if (errors && typeof errors === 'object') {
      // Check if errors has the structure { forms: {...}, files: {...} }
      if ('forms' in errors || 'files' in errors) {
        const errorObj = errors as { forms?: Record<string, string>; files?: Record<string, string> };
        
        // Extract form errors from forms object
        if (errorObj.forms && typeof errorObj.forms === 'object') {
          Object.entries(errorObj.forms).forEach(([formId, message]) => {
            if (!formErrors[formId]) {
              formErrors[formId] = [];
            }
            formErrors[formId].push(normalizeErrorMessage(message));
          });
        }
        
        // Extract file errors from files object - always show as separate groups
        if (errorObj.files && typeof errorObj.files === 'object') {
          Object.entries(errorObj.files).forEach(([fileIdStr, message]) => {
            const fileId = parseInt(fileIdStr, 10);
            fileErrors[fileIdStr] = {
              fileId,
              message: normalizeErrorMessage(message),
            };
          });
        }
      } else {
        // Normal case: errors is already { [formId]: message }
        Object.entries(errors).forEach(([formId, message]) => {
          if (!formErrors[formId]) {
            formErrors[formId] = [];
          }
          const normalized = normalizeErrorMessage(
            typeof message === 'string'
              ? message
              : message && typeof message === 'object'
                ? (message as Record<string, unknown>)
                : String(message)
          );
          formErrors[formId].push(normalized);
        });
      }
    }

    const groups: TerminalErrorGroup[] = [];

    // Process form errors
    Object.entries(formErrors).forEach(([studyFormId, messages]) => {
      const formId = parseInt(studyFormId, 10);
      const form = studyForms?.get(formId);

      // Debug logging
      if (!form && studyForms && studyForms.size > 0) {
        console.warn(
          `⚠️ Form ${formId} not found in studyForms map. Available IDs:`,
          Array.from(studyForms.keys())
        );
      }

      const formName = form ? form.name : `Form #${studyFormId}`;

      groups.push({
        id: `form-${studyFormId}`,
        label: form ? (
          <StudyFormHoverCard form={form} allForms={studyForms}>
            <span>{formName}</span>
          </StudyFormHoverCard>
        ) : (
          formName
        ),
        badge: (
          <Badge color="red" variant="soft" size="small">
            Form ID: {studyFormId}
          </Badge>
        ),
        errors: messages.map((message, index) => ({
          id: `form-${studyFormId}-error-${index}`,
          label: `Form #${studyFormId}`,
          message,
        })),
      });
    });

    // Process file errors - always show as separate groups
    Object.values(fileErrors).forEach(({ fileId, message }) => {
      const csvFile = csvFiles?.get(fileId);
      const fileName = csvFile?.filename || `File #${fileId}`;
      
      groups.push({
        id: `file-${fileId}`,
        label: fileName,
        badge: (
          <Badge color="red" variant="soft" size="small">
            File ID: {fileId}
          </Badge>
        ),
        errors: [
          {
            id: `file-${fileId}-error`,
            label: `File #${fileId}`,
            message,
          },
        ],
      });
    });

    return groups;
  }, [errors, studyForms, csvFiles]);

  const totalErrors = useMemo(() => {
    return errorGroups.reduce((sum, group) => sum + group.errors.length, 0);
  }, [errorGroups]);

  if (totalErrors === 0) {
    return null;
  }

  return (
    <TerminalErrorDisplay
      title={
        <Callout
          color="error"
          variant="surface"
          icon={<IconAlertTriangle />}
          title={
            <Flex align="center" gap="2">
              <Text>Import Validation Errors</Text>
              <Badge color="red" variant="soft" size="small">
                {totalErrors} error{totalErrors !== 1 ? 's' : ''}
              </Badge>
            </Flex>
          }
        />
      }
      groups={errorGroups}
    />
  );
};
